/**
 * GhostDashSkillEffect — Specter wraith-path Evasion upgrade: silent dashing.
 *
 * Upgrade enhancing Blink (via tree position), but its mechanical scope is
 * global: on equip, dashing while concealed no longer generates detection
 * events for ANY dash type (normal dash, Blink, Wraith/Flicker). Two
 * independent flags work in concert:
 *
 * - `suppressDashBreaksStealth` — dashing does not break Hide/Stealth
 * - `suppressDashGeneratesNoise` — dashing does not trigger enemy sound cones
 *
 * Together these make the entire dash vocabulary stealth-safe.
 *
 * On equip: sets both flags on DashComponent.
 * On unequip: resets both flags.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-drift-path-proposal.md § Ghost Dash
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface GhostDashDeps {
  readonly dash: {
    /** When true, dashing does not break Hide/Stealth. */
    suppressDashBreaksStealth: boolean;
    /** When true, dashing does not generate sound-cone detection events. */
    suppressDashGeneratesNoise: boolean;
  };
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class GhostDashSkillEffect implements SkillEffect {
  readonly skillId = 'ghost_dash';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;
  readonly staminaCost: number = 0;

  private readonly deps: GhostDashDeps;

  /** Whether the flags have been applied (guards against double-application). */
  private _applied = false;

  constructor(deps: GhostDashDeps) {
    this.deps = deps;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  deactivate(): void {}
  isActive(): boolean { return false; }

  update(_delta: number): void {
    // Apply both suppression flags on first update tick (equip).
    if (!this._applied) {
      this.deps.dash.suppressDashBreaksStealth = true;
      this.deps.dash.suppressDashGeneratesNoise = true;
      this._applied = true;
    }
  }

  destroy(): void {
    // Reset both flags on unequip
    if (this._applied) {
      this.deps.dash.suppressDashBreaksStealth = false;
      this.deps.dash.suppressDashGeneratesNoise = false;
      this._applied = false;
    }
  }
}
